import * as React from "react";
import BoardCard from "src/components/shared/Cards/BoardCard";
import { displayErrorToast } from "src/components/shared/Toasts/Toast";
import {
  UserCardsWrapper,
  UserDeckScrollButton,
  UserDeckText,
  UserDeckWrapper,
} from "src/components/UserDeck/UserDeck.components";
import { ToastIds } from "src/enums/errorToastIds";
import { UserRole } from "src/enums/user/userRoles";
import { useGameContext } from "src/game/useGameContext";
import { ReactComponent as AngleLeft } from "src/images/angleLeft.svg";
import { ReactComponent as AngleRight } from "src/images/angleRight.svg";

export interface Props {
  isDeckHidden?: boolean;
}

const SCROLL_SPEED = 60;

const UserDeck: React.FunctionComponent<Props> = ({ isDeckHidden }) => {
  const [selectedCardIndex, setSelectedCardIndex] = React.useState<number>();
  const [scrollIndicatorsVisible, setScrollIndicatorsVisible] =
    React.useState<boolean>();

  const {
    gameState,
    actionHandlers: { castVote, chooseFinalResult },
  } = useGameContext();

  const hasCardSelected: boolean =
    gameState.game?.task?.votes?.findIndex(
      vote => gameState.user?.id === vote.userId
    ) !== -1 ||
    gameState.user?.vote !== undefined ||
    !!gameState.voteReveal?.finalResult;

  const isSelectingFinalResult =
    gameState.voteReveal && !gameState.voteReveal.finalResult;

  const handleCardSelection = async (cardValue: string, cardIndex: number) => {
    if (!gameState.token || !gameState.game) {
      return;
    }
    const authData = {
      gameId: gameState.game?.id,
      token: gameState.token,
    };
    const sameCardSelected = cardIndex === selectedCardIndex;
    try {
      if (
        gameState.user?.role == UserRole.OWNER &&
        (isSelectingFinalResult || !!gameState.voteReveal?.finalResult)
      ) {
        await chooseFinalResult({
          gameId: gameState.game.id,
          token: gameState.token,
          taskId: gameState.game.task?.id ?? "",
          finalResult: cardValue as string,
        });

        setSelectedCardIndex(cardIndex);

        return;
      }

      if (hasCardSelected && sameCardSelected) {
        await castVote({
          gameId: gameState.game.id,
          token: gameState.token,
          vote: null,
        });
        setSelectedCardIndex(undefined);
      } else {
        const response = await castVote({
          ...authData,
          vote: {
            value: cardValue,
          },
        });
        if (response.error) {
          displayErrorToast(
            `Couldn't send your vote ${response.error.message}`,
            ToastIds.CAST_VOTE_FAILED
          );
        } else {
          setSelectedCardIndex(cardIndex);
        }
      }
    } catch (e) {
      displayErrorToast(
        `Couldn't send your vote ${e}`,
        ToastIds.CAST_VOTE_FAILED
      );
    }
  };

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setSelectedCardIndex(undefined);
  }, [gameState.voteReveal, gameState.user?.isSpectator]);

  React.useEffect(() => {
    const callback = () => {
      if (wrapperRef.current) {
        const cards = wrapperRef.current.children;
        const cardsWidth =
          cards[cards.length - 1].getBoundingClientRect().x +
          cards[cards.length - 1].getBoundingClientRect().width -
          cards[0].getBoundingClientRect().x;

        setScrollIndicatorsVisible(cardsWidth + 100 > window.innerWidth);
      }
    };

    callback();

    window.addEventListener("resize", callback);

    return () => removeEventListener("resize", callback);
  }, [wrapperRef.current]);

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollLeft += e.deltaY;
    }
  };

  const handleScrollRight = () => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollLeft += SCROLL_SPEED;
    }
  };
  const handleScrollLeft = () => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollLeft -= SCROLL_SPEED;
    }
  };

  const cardsOptions = gameState.game?.votingSystem.values.map((v, i) => (
    <BoardCard
      key={v}
      value={v}
      isActive={
        (selectedCardIndex === i && hasCardSelected) ||
        v === gameState.user?.vote?.value
      }
      onSelection={() => handleCardSelection(v, i)}
    />
  ));

  return (
    <UserDeckWrapper isVisible={!isDeckHidden} data-cy="userDeck">
      <UserDeckScrollButton
        onClick={handleScrollLeft}
        visible={scrollIndicatorsVisible}>
        <AngleLeft />
      </UserDeckScrollButton>
      <UserDeckScrollButton
        onClick={handleScrollRight}
        visible={scrollIndicatorsVisible}>
        <AngleRight />
      </UserDeckScrollButton>
      <UserDeckText>
        {(isSelectingFinalResult || !!gameState.voteReveal?.finalResult) &&
        gameState.user?.role == UserRole.OWNER
          ? "Choose final result"
          : "Choose your card"}
      </UserDeckText>
      <UserCardsWrapper onWheel={handleWheel} ref={wrapperRef}>
        {cardsOptions}
      </UserCardsWrapper>
    </UserDeckWrapper>
  );
};

export default UserDeck;
